import type { FixedComponentDefinition, MapDefinition, MapId, RackDefinition, RackType } from "../domain/types";
import basementSourceJson from "./facility-maps/basement.map.json";
import garageSourceJson from "./facility-maps/garage.map.json";
import smallCampusSourceJson from "./facility-maps/small-campus.map.json";
import smallColoSourceJson from "./facility-maps/small-colo.map.json";
import type { FacilityMapSource } from "./facilityMapSource";

const basementSource = basementSourceJson as FacilityMapSource;
const garageSource = garageSourceJson as FacilityMapSource;
const smallCampusSource = smallCampusSourceJson as FacilityMapSource;
const smallColoSource = smallColoSourceJson as FacilityMapSource;

function rackType(capacityU: 4 | 24 | 42): RackType {
    if (capacityU === 4) {
        return "wall-4u";
    }
    if (capacityU === 24) {
        return "mid-24u";
    }
    return "full-42u";
}

function sortedCoordinates(values: readonly number[]): readonly number[] {
    return [...new Set(values)].sort((left, right) => left - right);
}

function smallColoRackIdentity(
    source: FacilityMapSource,
    rack: FacilityMapSource["rackSlots"][number],
): Pick<RackDefinition, "id" | "name"> {
    const columns = sortedCoordinates(source.rackSlots.map((slot) => slot.x));
    const rows = sortedCoordinates(source.rackSlots.map((slot) => slot.y));
    const columnIndex = columns.indexOf(rack.x);
    const rowIndex = rows.indexOf(rack.y);
    if (columnIndex < 0 || rowIndex < 0 || columnIndex > 25) {
        throw new Error(`Unable to resolve Small Colo rack identity for ${rack.id}.`);
    }
    const number = columnIndex * rows.length + rowIndex + 1;
    return {
        id: `small-colo-rack-${String(number).padStart(2, "0")}`,
        name: `Colo ${String.fromCharCode(65 + columnIndex)}${rowIndex + 1}`,
    };
}

function basementRackIdentity(
    source: FacilityMapSource,
    rack: FacilityMapSource["rackSlots"][number],
): Pick<RackDefinition, "id" | "name"> {
    if (rack.type === "wall") {
        return { id: "basement-wall-rack-01", name: "Wall Rack W1" };
    }
    const standingRacks = source.rackSlots
        .filter((slot) => slot.type === "standing")
        .sort((left, right) => left.x - right.x || left.y - right.y);
    const rackIndex = standingRacks.findIndex((slot) => slot.id === rack.id);
    if (rackIndex < 0) {
        throw new Error(`Unable to resolve Basement rack identity for ${rack.id}.`);
    }
    return {
        id: `basement-rack-${String(rackIndex + 1).padStart(2, "0")}`,
        name: `Basement R${rackIndex + 1}`,
    };
}

function garageRackIdentity(
    source: FacilityMapSource,
    rack: FacilityMapSource["rackSlots"][number],
): Pick<RackDefinition, "id" | "name"> {
    if (rack.type === "wall") {
        return { id: "garage-wall-rack-01", name: "Garage Wall Rack" };
    }
    const standingRacks = source.rackSlots
        .filter((slot) => slot.type === "standing")
        .sort((left, right) => (left.slot ?? 0) - (right.slot ?? 0));
    const rackIndex = standingRacks.findIndex((slot) => slot.id === rack.id);
    if (rackIndex < 0) {
        throw new Error(`Unable to resolve Garage rack identity for ${rack.id}.`);
    }
    return {
        id: `garage-rack-${String(rackIndex + 1).padStart(2, "0")}`,
        name: `Garage R${rackIndex + 1}`,
    };
}

function smallCampusRackIdentity(rack: FacilityMapSource["rackSlots"][number]): Pick<RackDefinition, "id" | "name"> {
    if (rack.slot === undefined || !Number.isSafeInteger(rack.slot) || rack.slot < 0) {
        throw new Error(`Unable to resolve Small Campus rack identity for ${rack.id}.`);
    }
    const rackNumber = rack.slot + 1;
    return {
        id: `small-campus-rack-${String(rackNumber).padStart(3, "0")}`,
        name: `Campus R${rackNumber}`,
    };
}

function rackIdentity(
    source: FacilityMapSource,
    mapId: MapId,
    rack: FacilityMapSource["rackSlots"][number],
): Pick<RackDefinition, "id" | "name"> {
    if (mapId === "small-colo") {
        return smallColoRackIdentity(source, rack);
    }
    if (mapId === "small-campus") {
        return smallCampusRackIdentity(rack);
    }
    if (mapId === "garage") {
        return garageRackIdentity(source, rack);
    }
    return basementRackIdentity(source, rack);
}

function fixedEquipment(
    mapId: MapId,
    rack: FacilityMapSource["rackSlots"][number],
): readonly FixedComponentDefinition[] | undefined {
    if (rack.eddSlotU === undefined) {
        return undefined;
    }
    if (mapId === "garage") {
        return [
            {
                id: "garage-wall-rack-01-fixed-edd",
                componentId: "edd-1x1g-wall-ont",
                startU: rack.eddSlotU,
                localName: "Garage EDD",
                canRename: false,
            },
        ];
    }
    if (mapId === "basement") {
        return [
            {
                id: "basement-wall-rack-01-fixed-edd",
                componentId: "edd-2x1g-residential-gateway",
                startU: rack.eddSlotU,
                localName: "Basement EDD",
                canRename: false,
            },
        ];
    }
    const azId = rack.azId;
    if (azId === undefined) {
        throw new Error(`${rack.id} has an EDD slot without an availability zone.`);
    }
    const isCampus = mapId === "small-campus";
    return [
        {
            id: `${mapId}-az${azId}-fixed-edd`,
            componentId: isCampus ? "edd-4x100g-mmr-cross-connect" : "edd-4x25g-1u-fibre-cross-connect",
            startU: rack.eddSlotU,
            localName: `AZ${azId} EDD`,
            canRename: false,
        },
    ];
}

function createMap(source: FacilityMapSource, mapId: MapId, sourceMapId: string = mapId): MapDefinition {
    if (source.schema !== "uptime-facility-map" || source.mapId !== sourceMapId) {
        throw new Error(`Invalid generated facility map for ${mapId}.`);
    }
    const unit = source.gridUnitPixels;
    const width = source.widthUnits * unit;
    const height = source.heightUnits * unit;
    const availabilityZoneIds = new Map(source.availabilityZones.map((zone) => [zone.azId, zone.id]));
    const racks = source.rackSlots.map((rack) => {
        const identity = rackIdentity(source, mapId, rack);
        const fixedComponents = fixedEquipment(mapId, rack);
        const availabilityZoneId = rack.azId === undefined ? undefined : availabilityZoneIds.get(rack.azId);
        if (rack.azId !== undefined && availabilityZoneId === undefined) {
            throw new Error(`${rack.id} references unknown availability zone ${rack.azId}.`);
        }
        return {
            ...identity,
            ...(availabilityZoneId === undefined ? {} : { availabilityZoneId }),
            type: rackType(rack.capacityU),
            sizeU: rack.capacityU,
            x: rack.x * unit,
            y: rack.y * unit,
            width: rack.width * unit,
            depth: rack.depth * unit,
            rotation: rack.rotation,
            ...(rack.slot === undefined ? {} : { gameSlot: rack.slot }),
            ...(fixedComponents === undefined ? {} : { fixedComponents }),
        } satisfies RackDefinition;
    });
    return {
        id: mapId,
        name: source.mapName,
        width,
        height,
        gridSize: unit,
        room: { x: 0, y: 0, width, height, label: `${source.mapName} facility` },
        racks,
        zones: source.availabilityZones.map((zone) => ({
            id: zone.id,
            name: zone.label,
            x: zone.x * unit,
            y: zone.y * unit,
            width: zone.width * unit,
            height: zone.height * unit,
            color: zone.color,
        })),
        areas: source.zones.map((zone) => ({
            id: zone.id,
            name: zone.label,
            x: zone.x * unit,
            y: zone.y * unit,
            width: zone.width * unit,
            height: zone.height * unit,
            color: zone.color,
            borderOpacity: zone.borderOpacity ?? 65,
            borderStyle: zone.borderStyle ?? "dashed",
            borderWidth: (zone.borderWidth ?? 0.125) * unit,
            fillOpacity: zone.fillOpacity ?? 9,
            fontSize: (zone.fontSize ?? 0.8) * unit,
            labelPosition: zone.labelPosition ?? "center",
            lineHeight: zone.lineHeight ?? 1.2,
            maxLines: zone.maxLines ?? 4,
            maxWidth: (zone.maxWidth ?? 12) * unit,
            wrapMode: zone.wrapMode ?? "none",
        })),
        walls: source.walls.map((wall) => ({
            ...wall,
            x1: wall.x1 * unit,
            x2: wall.x2 * unit,
            y1: wall.y1 * unit,
            y2: wall.y2 * unit,
        })),
        lines: source.lines.map((line) => ({
            ...line,
            thickness: line.thickness * unit,
            x1: line.x1 * unit,
            x2: line.x2 * unit,
            y1: line.y1 * unit,
            y2: line.y2 * unit,
        })),
        badges: source.badges.map((badge) => ({
            id: badge.id,
            name: badge.label,
            x: badge.x * unit,
            y: badge.y * unit,
            width: badge.width * unit,
            height: badge.height * unit,
            borderStyle: badge.borderStyle,
            borderThickness: badge.borderThickness * unit,
            color: badge.color,
            cornerRadius: badge.cornerRadius * unit,
            fillOpacity: badge.fillOpacity,
            fontSize: badge.fontSize * unit,
            textColor: badge.textColor,
        })),
        labels: source.labels.map((label) => ({
            id: label.id,
            name: label.label,
            x: label.x * unit,
            y: label.y * unit,
            alignment: label.alignment,
            color: label.color,
            fontSize: label.fontSize * unit,
            lineHeight: label.lineHeight ?? 1.2,
            maxLines: label.maxLines ?? 4,
            maxWidth: (label.maxWidth ?? 12) * unit,
            wrapMode: label.wrapMode ?? "none",
        })),
    };
}

export const MAP_DEFINITIONS = Object.freeze([
    createMap(garageSource, "garage"),
    createMap(basementSource, "basement"),
    createMap(smallColoSource, "small-colo"),
    createMap(smallCampusSource, "small-campus", "small_campus"),
] as const satisfies readonly MapDefinition[]);

export function getMaps(): readonly MapDefinition[] {
    return MAP_DEFINITIONS;
}

export function getMap(mapId: string): MapDefinition | undefined {
    return getMaps().find((map) => map.id === mapId);
}

export function getRack(map: MapDefinition, rackId: string): RackDefinition | undefined {
    return map.racks.find((rack) => rack.id === rackId);
}
